import { useState } from "react";
import { Link, useLocation, Outlet } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  FolderPlus,
  Palette,
  Users,
  FolderOpen,
  Music,
  Globe,
  CreditCard,
  Settings,
  UserCircle,
  HelpCircle,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Heart,
  Menu,
  X,
} from "lucide-react";
import { Avatar } from "@/components/ui/Avatar";
import { Button } from "@/components/ui/Button";
import { useAuthStore } from "@/stores/authStore";
import { cn } from "@/lib/utils";
import { ROUTES } from "@/lib/constants";

const sidebarLinks = [
  { icon: LayoutDashboard, label: "Мои сайты", href: ROUTES.PROJECTS },
  { icon: FolderPlus, label: "Создать сайт", href: ROUTES.CREATE },
  { icon: Palette, label: "Шаблоны", href: ROUTES.TEMPLATES },
  { icon: Users, label: "Ответы гостей", href: ROUTES.GUESTS },
  { icon: FolderOpen, label: "Файлы", href: ROUTES.FILES },
  { icon: Globe, label: "Домены", href: ROUTES.DOMAINS },
  { icon: CreditCard, label: "Оплата", href: ROUTES.BILLING },
  { icon: Settings, label: "Настройки", href: ROUTES.SETTINGS },
  { icon: UserCircle, label: "Профиль", href: ROUTES.PROFILE },
  { icon: HelpCircle, label: "Поддержка", href: ROUTES.SUPPORT },
];

export function DashboardLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuthStore();

  const SidebarContent = ({ isMobile = false }) => (
    <>
      {/* Logo */}
      <div className="p-6 border-b border-cream">
        <Link to={ROUTES.HOME} className="flex items-center gap-2">
          <Heart className="w-6 h-6 text-gold fill-gold" />
          {(!collapsed || isMobile) && (
            <span className="text-xl font-display font-semibold text-dark-chocolate">
              WeddInv
            </span>
          )}
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {sidebarLinks.map((link) => {
          const isActive = location.pathname === link.href;
          return (
            <Link
              key={link.href}
              to={link.href}
              onClick={() => isMobile && setMobileOpen(false)}
              className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-body font-medium transition-all duration-200",
                isActive
                  ? "bg-gold/10 text-gold"
                  : "text-warm-gray hover:bg-cream hover:text-dark-chocolate",
                collapsed && !isMobile && "justify-center px-3"
              )}
              title={collapsed && !isMobile ? link.label : undefined}
            >
              <link.icon size={20} />
              {(!collapsed || isMobile) && <span>{link.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* User Section */}
      <div className="p-4 border-t border-cream">
        <div
          className={cn(
            "flex items-center gap-3",
            collapsed && !isMobile && "justify-center"
          )}
        >
          <Avatar
            name={user?.full_name || "User"}
            src={user?.avatar_url}
            size="md"
          />
          {(!collapsed || isMobile) && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-body font-medium text-dark-chocolate truncate">
                {user?.full_name || "Пользователь"}
              </p>
              <p className="text-xs font-body text-soft-gray truncate">
                {user?.email}
              </p>
            </div>
          )}
        </div>
        {(!collapsed || isMobile) && (
          <button
            onClick={logout}
            className="flex items-center gap-3 w-full mt-4 px-4 py-3 rounded-xl text-sm font-body font-medium text-warm-gray hover:bg-cream hover:text-dark-chocolate transition-colors"
          >
            <LogOut size={20} />
            <span>Выйти</span>
          </button>
        )}
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-ivory">
      {/* Desktop Sidebar */}
      <aside
        className={cn(
          "hidden lg:flex flex-col fixed inset-y-0 left-0 z-40 bg-ivory border-r border-cream transition-all duration-300",
          collapsed ? "w-20" : "w-64"
        )}
      >
        <SidebarContent />
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-ivory border border-cream rounded-full flex items-center justify-center text-soft-gray hover:text-dark-chocolate transition-colors"
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-ivory border-b border-cream">
        <div className="flex items-center justify-between px-4 h-16">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 rounded-xl text-dark-chocolate hover:bg-cream transition-colors"
          >
            <Menu size={24} />
          </button>
          <Link to={ROUTES.HOME} className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-gold fill-gold" />
            <span className="text-lg font-display font-semibold text-dark-chocolate">
              WeddInv
            </span>
          </Link>
          <div className="w-10" />
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 z-50 bg-dark-chocolate/40"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="lg:hidden fixed inset-y-0 left-0 z-50 w-72 bg-ivory shadow-large flex flex-col"
            >
              <button
                onClick={() => setMobileOpen(false)}
                className="absolute top-4 right-4 p-2 rounded-xl text-soft-gray hover:text-dark-chocolate hover:bg-cream transition-colors"
              >
                <X size={20} />
              </button>
              <SidebarContent isMobile />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main
        className={cn(
          "transition-all duration-300",
          "lg:ml-64",
          collapsed && "lg:ml-20",
          "pt-16 lg:pt-0"
        )}
      >
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
